import { useCallback, useEffect } from 'react';
import { useAppStore, type StoredReply } from '@/stores/appStore';
import { generateUUID } from '@/utils/uuid';
import { apiClient, type ChatRequest, type ChatResponse } from '@/utils/apiClient';
import { reconcileClusterSelection, getKnownClusterIds } from '@/utils/clusterReconcile';
import { CHAT_MESSAGE_MAX_CHARS, isMessageTooLongError, messageTooLongText } from '@/utils/messageLimit';
import { getTranslations, type Language } from '@/i18n';
import { answeredByModel, failoverFrom } from '@/utils/answeredBy';

/** How a chat turn ended. 'busy': refused, the chat already had a turn in flight. */
export type ChatTurnOutcome = 'done' | 'error' | 'aborted' | 'busy';

export interface ChatTurn {
  /** The chat that sends, captured before any await. Every write goes to it. */
  sessionId: string;
  /** session_id is always `sessionId`; whatever the request names is overridden. */
  request: Omit<ChatRequest, 'session_id'>;
  /** Shown on the chat's indicator while the turn is an auto-retry. */
  retry?: { attempt: number };
  /** Runs once the turn is the chat's, before the request (add the user message). */
  onStart?: () => void | Promise<void>;
  /**
   * The reply. Not called when the turn was stopped or its chat closed.
   * STORY-524: `reply` names the answer by its turn and says whether the
   * server already stored it — pass it to addMessage.
   */
  onResponse: (response: ChatResponse, reply: StoredReply) => void;
  /** A failure. Not called for a stopped turn: Stop is not an error. */
  onError: (error: unknown) => void;
}

/**
 * STORY-299: one chat turn, addressed to the chat that sent it. Every send
 * path (input, slash-free retries, re-ask, execute-plan, tool interpretation)
 * goes through here, so each one gets the same rules:
 *
 * - one turn in flight per chat (a second send in it is refused); other chats
 *   are not blocked;
 * - the request carries the chat's AbortSignal: Stop, closing the chat and
 *   logout abort it, and a stopped turn writes nothing (no apology message);
 * - STORY-524/511: the request names its turn (`turn_id`), so Stop, closing
 *   the chat and logout stop the server's work too, and a reload finds it;
 * - the chat's counters are refreshed when its reply lands while another chat
 *   is open (the open chat refreshes its own on every answer, ChatInterface).
 */
export async function runChatTurn(turn: ChatTurn): Promise<ChatTurnOutcome> {
  const { sessionId } = turn;
  const store = useAppStore.getState();
  const turnId = generateUUID();
  const signal = store.beginTurn(sessionId, turn.retry, turnId, turn.request.server_save === true);
  if (!signal) return 'busy';
  try {
    await turn.onStart?.();
    if (signal.aborted) return 'aborted';
    const response = await apiClient.chat({ ...turn.request, session_id: sessionId, turn_id: turnId }, signal);
    // EQUIVALENT MUTANT (removing this line): fetch, and the body read in
    // handleResponse, reject once the signal aborts, so an aborted turn lands
    // in `catch`. Kept as a guard for a client that resolves anyway.
    if (signal.aborted) return 'aborted';
    // STORY-511: the turn was cancelled on the server by another tab (it
    // closed this chat): an outcome with no answer, so nothing is written.
    if (response.stats?.path === 'cancelled') return 'aborted';
    turn.onResponse(response, { id: turnId, persisted: response.saved === true });
    // The open chat refreshes on its new answer already (ChatInterface's
    // effect); a chat answering in the background has no one else to.
    // EQUIVALENT MUTANT: refreshing unconditionally is behaviourally the same
    // apart from one duplicate GET for the open chat (dropped by the stale-
    // response guard in refreshSessionStats).
    if (useAppStore.getState().currentSession?.id !== sessionId) {
      void useAppStore.getState().refreshSessionStats(sessionId);
    }
    return 'done';
  } catch (error) {
    if (signal.aborted) return 'aborted';
    turn.onError(error);
    return 'error';
  } finally {
    useAppStore.getState().endTurn(sessionId, signal);
  }
}

export const useChatService = () => {
  const {
    addMessage,
    setError,
    llmProviders,
    mcpServers,
    settings,
    addSessionTokenUsage
  } = useAppStore();

  const sendMessage = useCallback(async (content: string) => {
    // STORY-299: the chat that sends is fixed here, before any await. The
    // reply, its tokens and its errors go to it even if another chat is open
    // by the time they arrive.
    const sid = useAppStore.getState().currentSession?.id;
    if (!sid) return;
    // One turn in flight per chat; another chat is never blocked by this one.
    // runChatTurn refuses too; this earlier check also keeps a refused send
    // from adding anything (the over-long message path adds without a turn).
    if (useAppStore.getState().pendingBySession[sid]) return;

    // In single mode, the selected provider doesn't need to be "enabled" (green toggle).
    // "enabled" means activated on the backend; "selected" means chosen for chat.
    const selectedInSingle = settings.llmMode === 'single' && settings.selectedLLMProvider
      ? llmProviders.find(p => p.id === settings.selectedLLMProvider)
      : null;
    const activeLLM = selectedInSingle || llmProviders.find(p => p.enabled);
    if (!activeLLM) {
      setError('No active LLM provider configured');
      return;
    }

    setError(null);

    // Slash commands: call tools directly
    const trimmed = content.trim();
    if (trimmed === '/get_runtime_license' || trimmed === '/get_runtime_config') {
      const signal = useAppStore.getState().beginTurn(sid);
      if (!signal) return;
      addMessage({ content, type: 'user' }, sid);
      try {
        const toolName = trimmed === '/get_runtime_license' ? 'get_runtime_license' : 'get_runtime_config';
        const result = await apiClient.callTool('Astromkey', toolName, {});
        if (signal.aborted) return;
        addMessage({
          content: typeof result === 'string' ? result : '```json\n' + JSON.stringify(result, null, 2) + '\n```',
          type: 'assistant',
          metadata: {
            model: activeLLM.model,
            toolCalls: [{ name: toolName, args: {}, status: 'ok', duration_ms: null }],
          },
        }, sid);
      } catch (err) {
        if (signal.aborted) return;
        const msg = err instanceof Error ? err.message : String(err);
        addMessage({ content: 'Tool call failed: ' + msg, type: 'assistant', metadata: { error: msg } }, sid);
        setError(msg, sid);
      } finally {
        useAppStore.getState().endTurn(sid, signal);
      }
      return;
    }

    // STORY-334: the backend refuses a message over CHAT_MESSAGE_MAX_CHARS
    // with a 422. Say so in the UI language instead of sending it.
    const tooLong = messageTooLongText(
      getTranslations((settings.language || 'ru') as Language).errors.messageTooLong,
      content.length);
    if (content.length > CHAT_MESSAGE_MAX_CHARS) {
      addMessage({ content, type: 'user' }, sid);
      addMessage({ content: tooLong, type: 'assistant', metadata: { error: 'message_too_long' } }, sid);
      return;
    }

    // Determine model preference based on settings
    let model_preference: string | undefined;
    if (settings.llmMode === 'single') {
      // Find the selected provider, or fall back to first enabled provider
      const selectedProvider = settings.selectedLLMProvider
        ? llmProviders.find(p => p.id === settings.selectedLLMProvider)
        : null;
      const effectiveProvider = selectedProvider || llmProviders.find(p => p.enabled);
      if (effectiveProvider) {
        // Format: provider_type-model_name (e.g., "ollama-qwen2.5:32b" or "gigachat-GigaChat-Max")
        model_preference = `${effectiveProvider.type}-${effectiveProvider.model}`;
      } else {
        // No provider available — fall back to auto
        model_preference = 'auto';
      }
    } else {
      // Auto mode
      model_preference = 'auto';
    }

    console.log(`[chat] model_preference=${model_preference}, llmMode=${settings.llmMode}, selectedProvider=${settings.selectedLLMProvider}`);
    // Call the backend API with language and model preference
    const chatRequest: Omit<ChatRequest, 'session_id'> = {
      content,
      language: settings.language || 'ru',
      model_preference,
      // STORY-524: the server stores this answer itself, so a reload while
      // it is computed does not lose it; the label is the one used below.
      server_save: true,
      ...(activeLLM.model ? { model_label: activeLLM.model } : {}),
    };

    // Include cluster routing fields whenever the user has a selection —
    // in EVERY mode, not just fan-out.
    // BUG-1.4.5_1: this block used to skip mode 'single' entirely ("let the
    // backend run single-cluster"), so a One-cluster selection was persisted
    // server-side but never sent with the query — the backend, receiving no
    // cluster fields, routed every call to the default cluster. A single
    // selection now travels as cluster_mode:'single' + its one id, which the
    // backend's STORY-252 routing consumes on both pipelines.
    // STORY-256 / BUG-1.4.0_2: reconcile against the last-known live cluster
    // ids so a stale selection never reaches the wire; a fan-out that
    // collapses to one survivor is sent as a single-route to that survivor
    // (not omitted — the backend cannot route to what it never receives).
    // STORY-299: copied into the request here, before the await, so changing
    // the selection while this chat waits only affects the next send.
    if (settings.clusterMode && settings.selectedClusterIds?.length) {
      const known = getKnownClusterIds();
      const r = known
        ? reconcileClusterSelection(settings.selectedClusterIds, known, settings.clusterMode)
        : { selectedClusterIds: settings.selectedClusterIds, clusterMode: settings.clusterMode, dropped: [] as string[], changed: false };
      if (r.clusterMode !== 'single' && r.selectedClusterIds.length >= 2) {
        chatRequest.cluster_mode = r.clusterMode as 'sequential' | 'parallel';
        chatRequest.cluster_ids = r.selectedClusterIds;
        chatRequest.synthesis_mode = (settings.synthesisModeOnMulti || 'synthesise') as 'synthesise' | 'concat';
      } else if (r.selectedClusterIds.length === 1) {
        chatRequest.cluster_mode = 'single';
        chatRequest.cluster_ids = [r.selectedClusterIds[0]];
      }
      if (r.dropped.length) {
        console.warn('[chat] dropped stale cluster ids before send:', r.dropped);
      }
    }

    await runChatTurn({
      sessionId: sid,
      request: chatRequest,
      // Add user message
      onStart: () => addMessage({ content, type: 'user' }, sid),
      onResponse: (response, reply) => {
        // Add assistant response — prefer the model the backend reports.
        // A recipe answer (stats.recipe_used) is deterministic and used no LLM,
        // so it gets no model label at all. Otherwise fall back to the provider's
        // human model name before the raw preference id: `model_preference` is
        // "<type>-<model>" (e.g. "gigachat-GigaChat-Max"), not a display name,
        // and 'auto' is the mode, not a model.
        // STORY-520: in AUTO mode the backend names the model that actually
        // answered (stats.answered_by) — it can differ from the default when
        // the first provider failed and the next one answered.
        // After a failover only stats.answered_by may name a model: without it
        // no model answered (every provider failed, or the next one refused),
        // and the default/active fallbacks would name one that did not.
        const usedRecipe = Boolean(response.stats?.recipe_used);
        const answeredBy = usedRecipe ? undefined : answeredByModel(response);
        const movedOff = usedRecipe ? [] : failoverFrom(response);
        const actualModel = usedRecipe
          ? undefined
          : answeredBy
            || (movedOff.length > 0
              ? undefined
              : response.token_usage?.model || response.token_usage?.provider
                || activeLLM.model
                || (model_preference && model_preference !== 'auto' ? model_preference : undefined));
        addMessage({
          content: response.content,
          type: 'assistant',
          metadata: {
            model: actualModel,
            ...(movedOff.length > 0
              ? { failoverFrom: movedOff, ...(answeredBy ? { answeredBy } : {}) }
              : {}),
            ...(response.tool_calls && { toolCalls: response.tool_calls }),
            ...(response.error && { error: response.error }),
            ...(response.token_usage && { tokens: response.token_usage.total_tokens }),
            ...(response.context_used && { contextUsed: true }),
            ...(response.multi_cluster && { multiCluster: response.multi_cluster }),
            ...(response.stats?.cluster_mention?.names?.length && {
              clusterMention: response.stats.cluster_mention,
            }),
            ...(response.pagination && { pagination: response.pagination }),
            ...(response.table_data && { tableData: response.table_data }),
            ...(response.chart_data?.length && { chartData: response.chart_data }),
            ...(response.alert_rule_proposal && { alertRuleProposal: response.alert_rule_proposal }),
            ...(response.incident_card && { incidentCard: response.incident_card }),
          },
          // STORY-524: chat_turns.assistant_metadata stores this same mapping
          // server-side — change the two together.
        }, sid, reply);

        // Track token usage for session
        if (response.token_usage) {
          addSessionTokenUsage(response.token_usage, sid);
        }

        // If there was an error in the response, set it
        if (response.error) {
          setError(response.error, sid);
        }
      },
      onError: (error) => {
        const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';

        if (isMessageTooLongError(error)) {
          // A limit this side does not know about (a proxy's 413, or a backend
          // configured lower): the same readable sentence, not an apology.
          addMessage({ content: tooLong, type: 'assistant', metadata: { error: errorMessage } }, sid);
          setError(errorMessage, sid);
        } else if (errorMessage.includes('fetch')) {
          // Check if backend is not available
          addMessage({
            content: 'I cannot connect to the backend service. Please make sure the backend is running on http://localhost:8080',
            type: 'assistant',
            metadata: {
              error: 'Backend connection failed',
            },
          }, sid);
          setError('Backend service not available', sid);
        } else {
          addMessage({
            content: 'I apologize, but I encountered an error while processing your request.',
            type: 'assistant',
            metadata: {
              error: errorMessage,
            },
          }, sid);
          setError(errorMessage, sid);
        }
      },
    });
  }, [addMessage, setError, llmProviders, mcpServers, settings, addSessionTokenUsage]);

  // Listen for command palette selections
  useEffect(() => {
    const onCmd = (e: Event) => {
      const ce = e as CustomEvent<string>;
      const cmd = ce.detail || '';
      if (cmd === '/get_runtime_license' || cmd === '/get_runtime_config') {
        // Delegate to slash handler by sending message
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        sendMessage(cmd);
      }
    };
    // @ts-ignore
    window.addEventListener('chat-command', onCmd);
    return () => {
      // @ts-ignore
      window.removeEventListener('chat-command', onCmd);
    };
  }, [sendMessage]);

  /**
   * STORY-299: Stop the open chat's turn — its request is aborted (the
   * browser stops waiting) and its reply writes nothing. Other chats go on.
   */
  const stopGeneration = useCallback(() => {
    const sid = useAppStore.getState().currentSession?.id;
    if (sid) useAppStore.getState().abortTurn(sid);
  }, []);

  return {
    sendMessage,
    stopGeneration,
  };
};
